import { useState } from 'react'
import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { runSandbox } from '../../api/services'
import type { Field, SandboxOutcome, ServiceDetail } from '../../api/services'
import { usePortal } from '../../auth/sessionContext'
import { CopyableId } from '../../components/ui/CopyableId'
import { Money } from '../../components/Money'
import { QueryError } from '../../components/QueryError'
import { StatusChip } from '../../components/ui/StatusChip'
import { UNSET, formatDuration } from '../../format'
import { errorEntry } from '../docs/content'
import { IMAGE_FIELD, exampleFor, fieldLabel, formatResultValue } from './contract'
import { SANDBOX_LABEL } from './labels'
import { Ltr } from '../../components/ui/Ltr'
import { toCanonicalNumber } from '../../numerals'

/**
 * A real call, made from the portal.
 *
 * The browser posts one payload to one portal endpoint and the *server* runs
 * the gateway: permission, idempotency, the wallet hold, routing, the upstream,
 * capture or release. Nothing here knows who serves the capability, and there
 * is no path from this component to anybody's provider — which is the point. A
 * Sandbox that took a shortcut would be showing a customer something the API
 * does not do, and it would put routing and billing inside a page they can
 * edit with a debugger.
 *
 * Because it is real, it spends money, and the banner says so before the button
 * is reachable rather than after.
 */
export function SandboxPanel({ service }: { service: ServiceDetail }) {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const queryClient = useQueryClient()

  const [applicationId, setApplicationId] = useState(service.applications[0]?.id ?? '')
  const [values, setValues] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(service.contract.request.map((f) => [f.name, exampleFor(f)])),
  )
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [outcome, setOutcome] = useState<SandboxOutcome | null>(null)

  const run = useMutation({
    mutationFn: () =>
      runSandbox(customerId, {
        application: applicationId,
        operation: service.code,
        payload: payloadFrom(service.contract.request, values),
        ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
      }),
    onSuccess: (result) => {
      setOutcome(result)
      // The call spent the wallet and wrote a request row, so both of those
      // screens are now stale.
      void queryClient.invalidateQueries({ queryKey: ['client', 'wallet'] })
      void queryClient.invalidateQueries({ queryKey: ['client', 'requests'] })
    },
  })

  const noApplication = service.applications.length === 0

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>{SANDBOX_LABEL.heading}</AlertTitle>
        {SANDBOX_LABEL.warning}
      </Alert>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2" gutterBottom>
          ورودی‌های فراخوانی
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          مقادیر اولیه ساختگی‌اند و به هیچ فرد واقعی تعلق ندارند. پیش از اجرا آن‌ها را با داده‌ای که
          خودتان اجازهٔ استعلامش را دارید جایگزین کنید.
        </Typography>

        <Stack spacing={2} sx={{ maxWidth: 520 }}>
          <TextField
            select
            fullWidth
            label="اجرا به نام کدام اپلیکیشن"
            value={applicationId}
            onChange={(event) => setApplicationId(event.target.value)}
            disabled={noApplication}
            helperText={
              noApplication
                ? 'هیچ اپلیکیشنی به این سرویس دسترسی ندارد.'
                : 'درخواست، دسترسی و هزینه به نام همین اپلیکیشن ثبت می‌شود.'
            }
          >
            {service.applications.map((application) => (
              <MenuItem key={application.id} value={application.id}>
                {application.name}
              </MenuItem>
            ))}
          </TextField>

          {service.contract.request.map((field) => (
            <InputField
              key={field.name}
              field={field}
              value={values[field.name] ?? ''}
              onChange={(next) => setValues((current) => ({ ...current, [field.name]: next }))}
            />
          ))}

          <TextField
            fullWidth
            label="کلید یکتایی (اختیاری)"
            value={idempotencyKey}
            onChange={(event) => setIdempotencyKey(event.target.value)}
            helperText="اگر همین کلید را دوباره بفرستید، پاسخ پیشین بازپخش می‌شود و بار دوم هزینه‌ای برداشت نمی‌شود."
          />
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={() => run.mutate()}
            disabled={run.isPending || noApplication || !applicationId}
          >
            اجرای فراخوانی
          </Button>
          {run.isPending ? <CircularProgress size={20} aria-label="در حال اجرا" /> : null}
        </Stack>
      </Paper>

      {run.isError ? <QueryError error={run.error} /> : null}

      {outcome ? (
        <SandboxResult service={service} outcome={outcome} />
      ) : run.isPending ? null : (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            هنوز فراخوانی‌ای انجام نشده است. پس از اجرا، پاسخ سرویس و هزینهٔ آن همین‌جا نمایش داده
            می‌شود.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

/** One request field, rendered as the control its type deserves. */
function InputField({
  field,
  value,
  onChange,
}: {
  field: Field
  value: string | boolean
  onChange: (next: string | boolean) => void
}) {
  if (field.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch checked={value === true} onChange={(event) => onChange(event.target.checked)} />
        }
        label={fieldLabel(field.name)}
      />
    )
  }

  // Numeric contract fields stay in ASCII on screen, because what is shown is
  // the request body the developer will reproduce in their own code — the one
  // place in this portal where the canonical form *is* the presentation.
  //
  // What they must not do is refuse the digits an Iranian keyboard produces.
  // `type="number"` did exactly that: a native number input silently discards
  // «۱۲۳», so the field stayed empty and the customer had no idea why.
  const numeric = field.type === 'integer' || field.type === 'number'
  return (
    <TextField
      fullWidth
      type="text"
      label={fieldLabel(field.name)}
      value={typeof value === 'boolean' ? '' : value}
      onChange={(event) =>
        onChange(
          numeric
            ? toCanonicalNumber(event.target.value, {
                allowNegative: true,
                decimals: field.type === 'number' ? 6 : 0,
              })
            : event.target.value,
        )
      }
      slotProps={numeric ? { htmlInput: { inputMode: 'decimal', dir: 'ltr' } } : undefined}
      required={field.required}
      // The contract's own name for the field, under the Persian label: it is
      // what the developer will type in their own code, and the two being on
      // screen together is what stops a mistranslation becoming a wrong key.
      helperText={field.name}
    />
  )
}

/**
 * What one run produced.
 *
 * A business rejection arrives as a 200 with `error` set, and it is rendered
 * here as an outcome rather than as a crash — because that is what it is. The
 * public code is shown beside the sentence: it is provider-neutral, it is what
 * the SDK raises, and it is what a support conversation quotes.
 */
function SandboxResult({ service, outcome }: { service: ServiceDetail; outcome: SandboxOutcome }) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 2 }}
      >
        <Typography variant="h2">نتیجهٔ فراخوانی</Typography>
        <StatusChip status={outcome.status} />
      </Stack>

      {outcome.replayed ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          این پاسخ از فراخوانی پیشین با همان کلید یکتایی بازپخش شد و بابت آن مبلغ تازه‌ای برداشت
          نشده است.
        </Alert>
      ) : null}

      {outcome.error ? <FailureAlert code={outcome.error.code} /> : null}

      {outcome.result ? (
        <Box sx={{ mb: 2 }}>
          <ResultFields fields={service.contract.result} value={outcome.result} />
        </Box>
      ) : null}

      {!outcome.result && !outcome.error ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          این فراخوانی محتوایی برای نمایش برنگرداند.
        </Typography>
      ) : null}

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1.5}>
        <Detail label="شناسهٔ درخواست">
          <CopyableId value={outcome.request_id} visible={20} label="شناسهٔ درخواست" />
        </Detail>
        <Detail label="شناسهٔ همبستگی">
          {outcome.correlation_id ? (
            <CopyableId value={outcome.correlation_id} visible={20} label="شناسهٔ همبستگی" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {UNSET.amount}
            </Typography>
          )}
        </Detail>
        <Detail label="مبلغ برداشت‌شده">
          <Money amountIrr={outcome.charged_amount_irr} whenUnknown={UNSET.amount} />
        </Detail>
        <Detail label="بهای فهرست‌شده">
          <Money amountIrr={outcome.sale_price_irr} whenUnknown={UNSET.salePrice} />
        </Detail>
        <Detail label="مدت پاسخ">
          <Typography variant="body2">{formatDuration(outcome.duration_ms)}</Typography>
        </Detail>
      </Stack>
    </Paper>
  )
}

/**
 * A refused call, said in Persian.
 *
 * The server's `message` is English developer prose, so what is shown is the
 * portal's own reading of the public code — the same words the technical guide
 * uses for it, including whether retrying is worth anything. The code itself
 * stays on screen, in Latin script and LTR: it is what the SDK raises and what
 * a support conversation quotes, and translating it would break both.
 */
function FailureAlert({ code }: { code: string }) {
  const entry = errorEntry(code)
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>درخواست پاسخ داده نشد</AlertTitle>
      {entry ? (
        <>
          <Typography variant="body2">{entry.meaning}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {entry.action}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">این فراخوانی انجام نشد.</Typography>
      )}
      <Typography variant="caption" component="p" sx={{ mt: 1 }}>
        کد خطا:{' '}
        <Ltr component="code" >
          {code}
        </Ltr>
      </Typography>
    </Alert>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}
      </Typography>
      {children}
    </Stack>
  )
}

/** The result, laid out along the contract the customer just read. */
function ResultFields({
  fields,
  value,
}: {
  fields: readonly Field[]
  value: Record<string, unknown>
}) {
  return (
    <Stack spacing={1.5}>
      {fields.map((field) => (
        <ResultRow key={field.name} field={field} value={value[field.name]} />
      ))}
    </Stack>
  )
}

function ResultRow({ field, value }: { field: Field; value: unknown }) {
  if (field.name === IMAGE_FIELD) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {fieldLabel(field.name)}
        </Typography>
        <PhotographBox value={value} />
      </Box>
    )
  }

  const children = field.children ?? []

  if (children.length > 0 && Array.isArray(value)) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {fieldLabel(field.name)}
        </Typography>
        {value.length === 0 ? (
          <Typography variant="body2">موردی برنگشت.</Typography>
        ) : (
          <Stack spacing={1}>
            {value.map((item, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <ResultFields fields={children} value={(item ?? {}) as Record<string, unknown>} />
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    )
  }

  if (children.length > 0 && value && typeof value === 'object') {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {fieldLabel(field.name)}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <ResultFields fields={children} value={value as Record<string, unknown>} />
        </Paper>
      </Box>
    )
  }

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {fieldLabel(field.name)}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {formatResultValue(field.name, value)}
      </Typography>
    </Stack>
  )
}

/**
 * The registry photograph.
 *
 * Bounded in both directions and `objectFit: contain`, because the payload is
 * whatever the registry returned and an unconstrained `<img>` with a tall
 * portrait in it pushes every value below it off the screen.
 */
function PhotographBox({ value }: { value: unknown }) {
  if (typeof value !== 'string' || value === '') {
    return (
      <Typography variant="body2" color="text.secondary">
        {UNSET.amount}
      </Typography>
    )
  }
  return (
    <Box
      sx={{
        width: 160,
        height: 200,
        overflow: 'hidden',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Box
        component="img"
        src={`data:image/png;base64,${value}`}
        alt="تصویر ثبت‌احوالی برگشتی از سرویس"
        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
    </Box>
  )
}

/** The form's values, converted to what the contract declares. */
function payloadFrom(
  fields: readonly Field[],
  values: Record<string, string | boolean>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const field of fields) {
    const raw = values[field.name]
    if (raw === undefined) continue
    if (field.type === 'boolean') {
      payload[field.name] = raw === true
      continue
    }
    if (field.type === 'integer' || field.type === 'number') {
      const text = String(raw).trim()
      // An empty numeric box is left out rather than sent as 0: zero is a value
      // somebody may have meant, and absence is not.
      if (text === '') continue
      const parsed = Number(text)
      payload[field.name] = Number.isNaN(parsed) ? text : parsed
      continue
    }
    payload[field.name] = String(raw)
  }
  return payload
}
